<template>
  <div class="chart-container" :style="{ height: `${height}px` }">
    <canvas ref="canvas"></canvas>
  </div>
</template>

<script>
// Import Chart.js
import Chart from "chart.js";

export default {
  props: {
    chartData: {
      type: Object,
      required: true,
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    height: {
      type: Number,
      default: 400,
    },
    selectedIndexes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      chart: null,
      internalSelectedIndexes: [...this.selectedIndexes],
    };
  },
  watch: {
    chartData: {
      handler() {
        this.renderChart();
      },
      deep: true,
    },
    options: {
      handler() {
        this.renderChart();
      },
      deep: true,
    },
    selectedIndexes: {
      handler(newVal) {
        this.internalSelectedIndexes = [...newVal];
        this.syncHiddenState();
      },
      deep: true,
    },
  },
  mounted() {
    this.renderChart();
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
    }
  },
  methods: {
    syncHiddenState() {
      if (!this.chart) return;
      // We are not hiding elements, just updating the chart to reflect prop changes
      // Visual feedback is handled by the parent via chartData colors
      this.chart.update();
    },

    renderChart() {
      try {
        // Destroy existing chart if it exists
        if (this.chart) {
          this.chart.destroy();
        }

        const optionsWithLegendClick = {
          ...this.options,
          legend: {
            ...this.options?.legend,
            labels: {
              ...this.options?.legend?.labels,
              generateLabels: (chart) => {
                const data = chart.data;
                if (data.labels.length && data.datasets.length) {
                  return data.labels.map((label, i) => {
                    const ds = data.datasets[0];
                    // Logic: if some items are selected, unselected ones get the cross (hidden: true)
                    const isSelected = this.internalSelectedIndexes.includes(i);
                    const showCross =
                      this.internalSelectedIndexes.length > 0 && !isSelected;

                    return {
                      text: label,
                      fillStyle: ds.backgroundColor[i],
                      strokeStyle: ds.borderColor[i],
                      lineWidth: ds.borderWidth,
                      hidden: showCross, // This adds the strikethrough in legend
                      index: i,
                    };
                  });
                }
                return [];
              },
            },
            onClick: (e, legendItem) => {
              const idx = legendItem.index;

              // toggle selection internally
              const i = this.internalSelectedIndexes.indexOf(idx);
              if (i > -1) this.internalSelectedIndexes.splice(i, 1);
              else this.internalSelectedIndexes.push(idx);

              this.chart.update();

              // emit all currently selected slices
              const selectedSlices = this.internalSelectedIndexes.map((i) => ({
                index: i,
                label: this.chartData.labels[i],
                value: this.chartData.datasets[0].data[i],
              }));

              this.$emit("chart-click", {
                selectedSlices,
                source: "legend",
              });
            },
          },
          onClick: (evt, activeElements) => {
            if (!activeElements.length) return;
            const clickedIndex = activeElements[0]._index;

            // toggle selection internally - BUT for slice click user usually expects single select
            // or toggle. Lead dashboard does single select on slice click.
            this.internalSelectedIndexes = [clickedIndex];

            this.chart.update();

            // emit all currently selected slices
            const selectedSlices = this.internalSelectedIndexes.map((i) => ({
              index: i,
              label: this.chartData.labels[i],
              value: this.chartData.datasets[0].data[i],
            }));

            this.$emit("chart-click", {
              selectedSlices,
              source: "slice",
              clickedIndex,
            });
          },
        };

        // Get the canvas context
        const ctx = this.$refs.canvas.getContext("2d");

        // Create a new chart
        this.chart = new Chart(ctx, {
          type: "pie",
          data: this.chartData,
          options: optionsWithLegendClick,
        });
      } catch (error) {
        console.error("Error rendering chart:", error);
      }
    },
  },
};
</script>

<style scoped>
.chart-container {
  position: relative;
  width: 100%;
  cursor: pointer;
}
</style>
